import type { Catalog, RecipeSummary, TaxonomyDoc } from "@/lib/types";

/**
 * Compute recipe-count overlays for BPMN element IDs.
 * Pure function — unit-testable without bpmn-js or DOM.
 *
 * Counts include descendants: a recipe tagged with
 * `source-to-pay/invoice-validation/three-way-match` counts toward:
 *   - source-to-pay
 *   - source-to-pay/invoice-validation
 *   - source-to-pay/invoice-validation/three-way-match
 */
export function computeRecipeCounts(
  catalog: Catalog,
  taxonomy: TaxonomyDoc,
): Map<string, number> {
  const counts = new Map<string, number>();
  const knownIds = new Set(taxonomy.nodes.map((n) => n.id));
  for (const r of catalog.recipes) {
    if (r.deprecated) continue;
    const counted = new Set<string>();
    for (const tag of r.process_tags) {
      const parts = tag.split("/");
      for (let i = 1; i <= parts.length; i++) {
        const prefix = parts.slice(0, i).join("/");
        if (knownIds.has(prefix) && !counted.has(prefix)) {
          counts.set(prefix, (counts.get(prefix) ?? 0) + 1);
          counted.add(prefix);
        }
      }
    }
  }
  return counts;
}

/** Return the recipes tagged to a node or any of its descendants. */
export function recipesForNode(
  catalog: Catalog,
  nodeId: string,
): RecipeSummary[] {
  return catalog.recipes.filter((r) =>
    r.process_tags.some((t) => t === nodeId || t.startsWith(`${nodeId}/`)),
  );
}
